import React, { useEffect, useState } from 'react';
import { Box, Paper, Button, TextField, IconButton, List, ListItemButton, ListItemText, Collapse, ListItemIcon, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Tooltip, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSnackbar } from '../context/SnackbarContext';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const DocumentsList: React.FC = () => {
    const [tree, setTree] = useState<any[]>([]);
    const [filtered, setFiltered] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const { exibirMensagem } = useSnackbar();

    const fetchDocs = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/plugsign/docs', { params: { _ts: Date.now() } });
            const items = res.data && res.data.tree ? res.data.tree : [];
            setTree(items);
            setFiltered(items);
        } catch (err: any) {
            console.error(err);
            exibirMensagem && exibirMensagem('Erro ao carregar documentos', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDocs(); }, [exibirMensagem]);

    useEffect(() => {
        const t = setTimeout(() => {
            const term = searchTerm.toLowerCase();
            if (!term) { setFiltered(tree); return; }
            const filterNode = (nodes: any[]): any[] => {
                const out: any[] = [];
                for (const n of nodes) {
                    const search = (node: any, prefix: string[] = []): boolean => {
                        const path = [...prefix, node.name].join('/');
                        if (node.file) {
                            if (path.toLowerCase().includes(term)) return true;
                        }
                        if (node.children) {
                            for (const c of node.children) if (search(c, [...prefix, node.name])) return true;
                        }
                        return false;
                    };

                    const matchesAny = search(n, []);
                    if (matchesAny) {
                        const clone = { ...n, children: filterNode(n.children || []) };
                        out.push(clone);
                    }
                }
                return out;
            };

            setFiltered(filterNode(tree));
        }, 250);
        return () => clearTimeout(t);
    }, [searchTerm, tree]);



    const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [selectedDeleteKey, setSelectedDeleteKey] = useState<string | null>(null);
    const [selectedDeleteName, setSelectedDeleteName] = useState<string | null>(null);
    const [selectedDeleteType, setSelectedDeleteType] = useState<'file' | 'folder' | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [selectedEditKey, setSelectedEditKey] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{ name: string; accessibility: string; folder?: string; status?: string }>({ name: '', accessibility: '', folder: '' });
    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderParent, setNewFolderParent] = useState<string | null>(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadFileState, setUploadFileState] = useState<File | null>(null);
    const [uploadForm, setUploadForm] = useState<{ tenantId?: string; productId?: string; autoCreate?: boolean; message?: string; width_page?: number; signers?: string; folder?: string; name?: string; accessibility?: string }>({ autoCreate: true, message: '', width_page: 1000, signers: '', name: '', accessibility: 'Everyone' });

    const collectFolderPaths = (nodes: any[], prefix = ''): string[] => {
        const out: string[] = [];
        for (const n of nodes || []) {
            if (!n) continue;
            const path = prefix ? `${prefix}/${n.name}` : n.name;
            if (n.type !== 'file') out.push(path);
            if (n.children && n.children.length) out.push(...collectFolderPaths(n.children, path));
        }
        return out;
    };

    const resolveDocKey = (node: any) => {
        if (!node) return null;
        const f = node.file || node;
        const cand = f.document_key || f.documentKey || f.key || f.id || f._id || f.documentId;
        if (cand) return String(cand);
        // fallback to path when key not available
        if (f.path) return String(f.path);
        if (f.name) return String(f.name);
        return null;
    };

    const findFolderIdByPath = (nodes: any[], path: string): any => {
        if (!path) return null;
        const parts = path.split('/');
        const walk = (ns: any[], idx: number): any => {
            for (const n of ns || []) {
                if (!n || n.type === 'file') continue;
                if (n.name === parts[idx]) {
                    if (idx === parts.length - 1) return n;
                    const res = walk(n.children || [], idx + 1);
                    if (res) return res;
                }
            }
            return null;
        };
        const node = walk(nodes, 0);
        if (!node) return null;
        return node.id || node.key || node._id || node.folderId || node.folder_id || null;
    };

    const toggleOpen = (key: string) => setOpenMap(prev => ({ ...prev, [key]: !prev[key] }));

    const handleConfirmDelete = async () => {
        if (!selectedDeleteKey || !selectedDeleteType) return;
        setDeleting(true);
        try {
            if (selectedDeleteType === 'file') {
                await api.delete(`/api/plugsign/docs/${encodeURIComponent(selectedDeleteKey)}`);
                exibirMensagem && exibirMensagem('Documento excluído com sucesso', 'success');
            } else {
                await api.delete(`/api/plugsign/folders/${encodeURIComponent(selectedDeleteKey)}`);
                exibirMensagem && exibirMensagem('Diretório excluído com sucesso', 'success');
            }
            setConfirmOpen(false);
            setSelectedDeleteKey(null);
            setSelectedDeleteName(null);
            setSelectedDeleteType(null);
            await fetchDocs();
        } catch (err: any) {
            console.error(err);
            exibirMensagem && exibirMensagem('Erro ao excluir', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const hasDocuments = (node: any): boolean => {
        if (!node) return false;
        if (node.file) return true;
        if (!node.children || !node.children.length) return false;
        for (const c of node.children) if (hasDocuments(c)) return true;
        return false;
    };

    const renderNodes = (nodes: any[], parentKey = '', depth = 0, currentPath = '') => {
        const pad = (d: number) => ({ paddingLeft: 16 + d * 16 });
        return nodes.map((node: any, idx: number) => {
            const key = `${parentKey}${parentKey ? '-' : ''}${node.name}-${idx}`;
            const nodePath = currentPath ? `${currentPath}/${node.name}` : node.name;
            if (node.file) {
                const fileId = encodeURIComponent((node.file && (node.file.path || node.file.name || node.file.filename || node.file.key || node.document_key || node.id)) || node.name);
                return (
                    <ListItemButton key={key} sx={{ display: 'flex', justifyContent: 'space-between' }} style={pad(depth)}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <ListItemIcon sx={{ minWidth: 36 }}><DescriptionIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary={node.name} />
                            {/* Accessibility lock icon */}
                            {(() => {
                                const access = node.file && (node.file.accessibility || node.file.access) || node.accessibility || node.access || '';
                                if (String(access).toLowerCase() === 'everyone') return <Tooltip title="Acessível a todos"><LockOpenIcon fontSize="small" sx={{ ml: 1 }} /></Tooltip>;
                                if (String(access).toLowerCase() === 'onlyme' || String(access).toLowerCase() === 'only_me') return <Tooltip title="Somente eu"><LockIcon fontSize="small" sx={{ ml: 1, color: 'error.main' }} /></Tooltip>;
                                if (access) return <Tooltip title={`Acesso: ${access}`}><LockIcon fontSize="small" sx={{ ml: 1 }} /></Tooltip>;
                                return null;
                            })()}
                            {/* Signed status check */}
                            {(() => {
                                const status = node.file && (node.file.status || node.status) || '';
                                if (String(status).toLowerCase() === 'signed') return <Tooltip title="Assinado"><CheckCircleIcon fontSize="small" sx={{ ml: 1, color: 'success.main' }} /></Tooltip>;
                                return null;
                            })()}
                        </div>
                        <span>
                            <Tooltip title="Editar documento"><IconButton size="small" onClick={(e) => { e.stopPropagation(); const docKey = resolveDocKey(node) || node.name; const folderForFile = currentPath || ''; setSelectedEditKey(docKey); setEditForm({ name: node.name || (node.file && (node.file.name || node.file.filename)), accessibility: (node.file && (node.file.accessibility || node.file.access)) || '', folder: folderForFile }); setEditOpen(true); }}><EditIcon /></IconButton></Tooltip>
                            <Tooltip title="Excluir documento"><IconButton size="small" onClick={(e) => { e.stopPropagation(); const docKey = resolveDocKey(node) || node.name; setSelectedDeleteType('file'); setSelectedDeleteKey(docKey); setSelectedDeleteName(node.name); setConfirmOpen(true); }}><DeleteIcon /></IconButton></Tooltip>
                            <Tooltip title="Visualizar"><IconButton size="small" onClick={() => navigate(`/assinaturas/documents/${fileId}`)}><VisibilityIcon /></IconButton></Tooltip>
                            <Tooltip title="Enviar para assinatura"><Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/assinaturas', { state: { documentUrl: decodeURIComponent(fileId) } })}>Enviar</Button></Tooltip>
                        </span>
                    </ListItemButton>
                );
            }
            const isOpen = !!openMap[key];
            const canDelete = !hasDocuments(node);
            return (
                <div key={key}>
                    <ListItemButton onClick={() => toggleOpen(key)} style={pad(depth)} sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <ListItemIcon sx={{ minWidth: 36 }}><FolderIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary={node.name} sx={{ fontWeight: 600 }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Editar diretório"><Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/assinaturas/folders/${encodeURIComponent(node.name)}/edit`); }}>Editar</Button></Tooltip>
                            <Tooltip title={canDelete ? 'Excluir diretório' : 'Diretório contém documentos'}><span><IconButton size="small" onClick={(e) => { e.stopPropagation(); if (canDelete) { setSelectedDeleteType('folder'); setSelectedDeleteKey(nodePath); setSelectedDeleteName(node.name); setConfirmOpen(true); } }} disabled={!canDelete}><DeleteIcon /></IconButton></span></Tooltip>
                            {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </div>
                    </ListItemButton>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {node.children && node.children.length ? renderNodes(node.children, key, depth + 1, nodePath) : null}
                        </List>
                    </Collapse>
                </div>
            );
        });
    };

    return (
        <Box sx={{ p: 2 }}>
            <TextField label="Buscar documentos" variant="outlined" fullWidth margin="normal" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <Button variant="contained" onClick={() => setUploadOpen(true)} sx={{ mb: 2, mr: 1 }}>Upload</Button>
            <Button variant="outlined" onClick={() => setNewFolderOpen(true)} sx={{ mb: 2 }}>Nova pasta</Button>
            <Paper sx={{ p: 2 }}>
                <List sx={{ maxHeight: 520, overflowY: 'auto' }}>
                    {renderNodes(filtered)}
                </List>
                <Dialog open={confirmOpen} onClose={() => { if (!deleting) setConfirmOpen(false); }}>
                    <DialogTitle>Confirmar exclusão</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Tem certeza que deseja excluir o {selectedDeleteType === 'folder' ? 'diretório' : 'documento'} "{selectedDeleteName || ''}"? Esta ação não pode ser desfeita.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancelar</Button>
                        <Button color="error" onClick={handleConfirmDelete} disabled={deleting} startIcon={deleting ? <CircularProgress size={16} /> : null}>Excluir</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={uploadOpen} onClose={() => { if (!uploading) setUploadOpen(false); }} fullWidth maxWidth="sm">
                    <DialogTitle>Upload de documento</DialogTitle>
                    <DialogContent>
                        <TextField select fullWidth margin="normal" label="Localização" value={uploadForm.folder || ''} onChange={e => setUploadForm(f => ({ ...f, folder: e.target.value }))}>
                            <MenuItem value="">(raiz)</MenuItem>
                            {(collectFolderPaths(tree) || []).map(fp => <MenuItem key={fp} value={fp}>{fp}</MenuItem>)}
                        </TextField>
                        <TextField fullWidth margin="normal" label="Nome" value={uploadForm.name || ''} onChange={e => setUploadForm(f => ({ ...f, name: e.target.value }))} />
                        <TextField select fullWidth margin="normal" label="Acessibilidade" value={uploadForm.accessibility || ''} onChange={e => setUploadForm(f => ({ ...f, accessibility: e.target.value }))}>
                            <MenuItem value="Everyone">Everyone</MenuItem>
                            <MenuItem value="OnlyMe">OnlyMe</MenuItem>
                            <MenuItem value="Private">Private</MenuItem>
                        </TextField>
                        <div style={{ marginTop: 12 }}>
                            <input id="doc-file-input" type="file" onChange={e => setUploadFileState(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setUploadOpen(false)} disabled={uploading}>Cancelar</Button>
                        <Button variant="contained" onClick={async () => {
                            if (!uploadFileState) { exibirMensagem && exibirMensagem('Selecione um arquivo', 'warning'); return; }
                            setUploading(true);
                            try {
                                const formData = new FormData();
                                formData.append('file', uploadFileState);
                                if (uploadForm.name) formData.append('name', uploadForm.name);
                                if (uploadForm.accessibility) formData.append('accessibility', uploadForm.accessibility);
                                // include folderId if possible (resolve path -> id)
                                if (uploadForm.folder) {
                                    const folderIdForUpload = findFolderIdByPath(tree, uploadForm.folder) || uploadForm.folder;
                                    formData.append('folderId', String(folderIdForUpload));
                                }
                                const upResp = await api.post('/api/signatures/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                                const fileUrl = upResp && upResp.data && upResp.data.url;
                                if (!fileUrl) throw new Error('Upload falhou');

                                exibirMensagem && exibirMensagem('Upload concluído', 'success');
                                setUploadOpen(false);
                                setUploadFileState(null);
                                setUploadForm({ autoCreate: true, message: '', width_page: 1000, signers: '' });
                                await fetchDocs();
                            } catch (err: any) {
                                console.error(err);
                                exibirMensagem && exibirMensagem('Erro no upload', 'error');
                            } finally {
                                setUploading(false);
                            }
                        }} disabled={uploading}>{uploading ? <CircularProgress size={16} /> : 'Enviar'}</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={editOpen} onClose={() => { if (!editLoading) setEditOpen(false); }} fullWidth maxWidth="sm">
                    <DialogTitle>Editar documento</DialogTitle>
                    <DialogContent>
                        <TextField fullWidth margin="normal" label="Nome" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                        <TextField select fullWidth margin="normal" label="Acessibilidade" value={editForm.accessibility} onChange={e => setEditForm(f => ({ ...f, accessibility: e.target.value }))}>
                            <MenuItem value="Everyone">Everyone</MenuItem>
                            <MenuItem value="OnlyMe">OnlyMe</MenuItem>
                            <MenuItem value="Private">Private</MenuItem>
                        </TextField>
                        <TextField select fullWidth margin="normal" label="Localização" value={editForm.folder || ''} onChange={e => setEditForm(f => ({ ...f, folder: e.target.value }))}>
                            {(collectFolderPaths(tree) || []).map(fp => <MenuItem key={fp} value={fp}>{fp}</MenuItem>)}
                        </TextField>
                        {editForm.status ? <TextField fullWidth margin="normal" label="Status" value={editForm.status} disabled /> : null}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditOpen(false)} disabled={editLoading}>Cancelar</Button>
                        <Button variant="contained" onClick={async () => {
                            if (!selectedEditKey) return;
                            setEditLoading(true);
                            try {
                                const payload: any = { name: editForm.name, accessibility: editForm.accessibility };
                                if (editForm.folder) {
                                    const folderId = findFolderIdByPath(tree, editForm.folder);
                                    if (folderId !== null && folderId !== undefined) {
                                        const num = Number(folderId);
                                        payload.folder = Number.isNaN(num) ? folderId : num;
                                    } else {
                                        payload.folder = editForm.folder; // fallback to path if id not found
                                    }
                                }
                                await api.put(`/api/plugsign/docs/${encodeURIComponent(selectedEditKey)}`, payload);
                                exibirMensagem && exibirMensagem('Documento atualizado', 'success');
                                setEditOpen(false);
                                await fetchDocs();
                            } catch (err: any) {
                                console.error(err);
                                exibirMensagem && exibirMensagem('Erro ao atualizar documento', 'error');
                            } finally {
                                setEditLoading(false);
                            }
                        }} disabled={editLoading}>{editLoading ? <CircularProgress size={16} /> : 'Salvar'}</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={newFolderOpen} onClose={() => { if (!creatingFolder) setNewFolderOpen(false); }} fullWidth maxWidth="sm">
                    <DialogTitle>Criar nova pasta</DialogTitle>
                    <DialogContent>
                        <TextField fullWidth margin="normal" label="Nome da pasta" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} />
                        <TextField select fullWidth margin="normal" label="Localização" value={newFolderParent || ''} onChange={e => setNewFolderParent(e.target.value)}>
                            {(collectFolderPaths(tree) || []).map(fp => <MenuItem key={fp} value={fp}>{fp}</MenuItem>)}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setNewFolderOpen(false)} disabled={creatingFolder}>Cancelar</Button>
                        <Button variant="contained" onClick={async () => {
                            if (!newFolderName) return;
                            setCreatingFolder(true);
                            try {
                                const payload: any = { name: newFolderName };
                                if (newFolderParent) payload.parent = newFolderParent;
                                await api.post('/api/plugsign/folders', payload);
                                exibirMensagem && exibirMensagem('Pasta criada', 'success');
                                setNewFolderOpen(false);
                                setNewFolderName('');
                                setNewFolderParent('');
                                await fetchDocs();
                            } catch (err: any) {
                                console.error(err);
                                exibirMensagem && exibirMensagem('Erro ao criar pasta', 'error');
                            } finally {
                                setCreatingFolder(false);
                            }
                        }} disabled={creatingFolder}>{creatingFolder ? <CircularProgress size={16} /> : 'Criar'}</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    );
};

export default DocumentsList;
